import type { Theme } from '@ksyos/design-system';
import type { Dispatch, JSX, SetStateAction } from 'react';
import type { DotProps } from 'recharts';
import { Alert } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import {
    Area,
    CartesianGrid,
    ComposedChart,
    Label,
    Line,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { useTheme } from 'styled-components';
import { ChartCircle } from '../../elements/chart/ChartCircle';
import { ChartTriangle } from '../../elements/chart/ChartTriangle';
import { MonitoringChartTooltip } from './MonitoringChartTooltip';

export interface IMonitoringChartItem {
    date: Date | string;
    difference: { days: number; months: number };
    median?: number;
    minMaxFirstBandwidth?: number[];
    minMaxSecondBandwidth?: number[];
    shape?: 'triangle' | 'circle';
    tooltipElement?: JSX.Element | null;
    value?: number;
}

export interface IMonitoringChartProps {
    /**
     * Hides tooltip on hover if enabled
     */
    disableTooltip?: boolean;
    /**
     * Grid of the chart
     */
    grid?: 'auto' | 'horizontal' | 'vertical';
    /**
     * Height of the chart
     */
    height: number;
    /**
     * Changes icon based on average
     */
    iconBasedOnAverage?: boolean;
    /**
     * Items provided to chart
     */
    items: IMonitoringChartItem[];
    /**
     * On dot click event
     */
    onDotClick?: (item: IMonitoringChartItem) => void;
    /**
     * Reference value to draw a reference line for (if the data has one value that should be tracked)
     */
    referenceValue?: number;
    /**
     * Selected item
     */
    selectedItem?: IMonitoringChartItem;
    /**
     * Displays a warning text instead of the chart
     */
    warningText?: string;
    /**
     * Label of X axis
     */
    XAxisLabel: string;
    /**
     * Ticks for X Axis
     */
    XAxisTicks?: number[];
    /**
     * Label of Y axis
     */
    YAxisLabel: string;
    /**
     * Ticks for Y Axis
     */
    YAxisTicks?: number[];
}

interface CustomDotProps extends DotProps {
    payload: IMonitoringChartItem;
}

function ActiveDot({
    setTooltipYCoordinate,
    tooltipYCoordinate,
}: Readonly<{
    setTooltipYCoordinate: Dispatch<SetStateAction<number>>;
    tooltipYCoordinate: number | undefined;
}>): JSX.Element {
    useEffect(() => {
        if (typeof tooltipYCoordinate === 'number') {
            setTooltipYCoordinate(tooltipYCoordinate - 40);
        }
    }, [tooltipYCoordinate]);

    return <></>;
}

export function MonitoringChart(props: Readonly<IMonitoringChartProps>): JSX.Element {
    const {
        XAxisLabel,
        XAxisTicks,
        YAxisLabel,
        YAxisTicks,
        disableTooltip,
        height,
        iconBasedOnAverage,
        items,
        onDotClick,
        referenceValue,
        selectedItem,
        warningText,
    } = props;
    // For unclear reasons this cast is necessary due to code in `PatientEpisodeCategoryTests.tsx`
    const theme = useTheme() as Theme;

    const [Ypos, setYpos] = useState(0);

    return (
        <>
            {warningText ? (
                <Alert
                    appearance="warning"
                    hasIcon={true}
                >
                    {warningText}
                </Alert>
            ) : (
                <ResponsiveContainer
                    height={height}
                    width="100%"
                >
                    <ComposedChart
                        data={items}
                        margin={{ bottom: 30, left: 35, top: 10 }}
                    >
                        <XAxis
                            allowDecimals={false}
                            dataKey="difference.months"
                            domain={['dataMin', 'dataMax']}
                            dy={5}
                            stroke={theme.colors.neutral700}
                            tick={{ fill: theme.colors.neutral700 }}
                            ticks={XAxisTicks}
                            tickSize={12}
                            type="number"
                        >
                            <Label
                                offset={-25}
                                position="insideBottom"
                                style={{
                                    fill: theme.colors.neutral700,
                                    fontSize: '14px',
                                }}
                                value={XAxisLabel}
                            />
                        </XAxis>
                        <YAxis
                            domain={['dataMin', (dataMax: number) => Math.ceil(dataMax / 10) * 10]}
                            dx={-5}
                            stroke={theme.colors.neutral700}
                            tick={{ fill: theme.colors.neutral700 }}
                            ticks={YAxisTicks}
                            tickSize={12}
                            type="number"
                        >
                            <Label
                                offset={-30}
                                position="insideLeft"
                                style={{
                                    fill: theme.colors.neutral700,
                                    fontSize: '14px',
                                }}
                                value={YAxisLabel}
                            />
                        </YAxis>
                        <ReferenceLine
                            stroke={theme.colors.neutral300}
                            y={0}
                        />
                        {props.grid && (
                            <CartesianGrid
                                horizontal={props.grid === 'horizontal'}
                                vertical={props.grid === 'vertical'}
                            />
                        )}
                        {!disableTooltip && (
                            <Tooltip
                                allowEscapeViewBox={{
                                    x: false,
                                    y: true,
                                }}
                                content={<MonitoringChartTooltip />}
                                cursor={false}
                                isAnimationActive={false}
                                offset={20}
                                position={{
                                    y: Ypos,
                                }}
                            />
                        )}
                        <Area
                            activeDot={false}
                            connectNulls={true}
                            dataKey="minMaxSecondBandwidth"
                            fill={theme.colors.neutral300}
                            isAnimationActive={false}
                            stroke="none"
                            type="linear"
                        />
                        <Area
                            activeDot={false}
                            connectNulls={true}
                            dataKey="minMaxFirstBandwidth"
                            fill={theme.colors.neutral100}
                            isAnimationActive={false}
                            stroke="none"
                            type="linear"
                        />
                        {referenceValue ? (
                            <ReferenceLine
                                stroke={theme.colors.neutral600}
                                strokeDasharray="8 8"
                                y={referenceValue}
                            />
                        ) : (
                            <Line
                                activeDot={false}
                                connectNulls={true}
                                dataKey="median"
                                dot={false}
                                isAnimationActive={false}
                                stroke={theme.colors.neutral600}
                                strokeDasharray="8 8"
                                type="linear"
                            />
                        )}
                        <Line
                            activeDot={({ cy }: DotProps) => (
                                <ActiveDot
                                    setTooltipYCoordinate={setYpos}
                                    tooltipYCoordinate={cy}
                                />
                            )}
                            connectNulls={true}
                            dataKey="value"
                            dot={renderDot}
                            isAnimationActive={false}
                            stroke={theme.colors.neutral600}
                            strokeWidth={2}
                            type="linear"
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            )}
        </>
    );

    function renderDot({ payload, key, cx, cy }: CustomDotProps) {
        const isActive = selectedItem === payload;
        if (payload.difference.months < 0 || payload.difference.days < 0) {
            return <div key={key} />;
        }
        const handleClick = () => {
            if (props.onDotClick) {
                props.onDotClick(payload);
            }
        };

        const Wrapper = ({ children, x, y }: { children: JSX.Element; x?: number; y?: number }) => (
            <svg
                fill="transparent"
                height="60"
                onClick={handleClick}
                style={{
                    cursor: onDotClick ? 'pointer' : 'default',
                    overflow: 'visible',
                }}
                viewBox="0 0 60 60"
                width="60"
                x={x && x - 10}
                y={y && y - 10}
            >
                <rect
                    height="40"
                    width="40"
                    x={-10}
                    y={-10}
                />
                {children}
            </svg>
        );

        if (iconBasedOnAverage) {
            if (typeof payload.value === 'number' && referenceValue) {
                if (payload.value >= referenceValue) {
                    return (
                        <Wrapper
                            key={key}
                            x={cx}
                            y={cy}
                        >
                            <ChartCircle
                                color="lime"
                                isActive={isActive}
                            />
                        </Wrapper>
                    );
                }

                return (
                    <Wrapper
                        key={key}
                        x={cx}
                        y={cy}
                    >
                        <ChartTriangle
                            color="red"
                            isActive={isActive}
                        />
                    </Wrapper>
                );
            }
        }
        if (typeof payload.value === 'number' && !(payload.difference.months < 1 && payload.difference.days === 0)) {
            if (payload.shape === 'triangle') {
                return (
                    <Wrapper
                        key={key}
                        x={cx}
                        y={cy}
                    >
                        <ChartTriangle
                            color="blue"
                            isActive={isActive}
                        />
                    </Wrapper>
                );
            }

            return (
                <Wrapper
                    key={key}
                    x={cx}
                    y={cy}
                >
                    <ChartCircle
                        color="magenta"
                        isActive={isActive}
                    />
                </Wrapper>
            );
        }

        // this function expects to return an element, so an empty div is provided if we don't want to show any kind of dot.
        return <div key={key} />;
    }
}
